import { AxiosResponse } from "axios";
import api from "../Interceptors/Interceptor";
import ApiResponse from "../Models/ApiResponse";
import { Page } from "../Models/Page";
import ReferenceModel from "../Models/ReferenceModel";
import { BaseService } from "./BaseService";

export type ProjectBlockStatus =
    | "CONCEPT"
    | "WAITING_DASHLY"
    | "WAITING_CUSTOMER"
    | "APPROVED";

export interface IProjectTechnicalFieldModel {
    uuid: string;
    fieldName: string;
    functionName?: string;
    groupBy?: boolean;
    sortOrder?: number;
    active?: boolean;
}

export interface IProjectTechnicalTableModel {
    uuid: string;
    datasetId: string;
    tableId: string;
    sortOrder?: number;
    active?: boolean;
    fields?: IProjectTechnicalFieldModel[];
}

export interface IProjectTechnicalMappingModel {
    uuid: string;
    tables?: IProjectTechnicalTableModel[];
    calculationConfig?: string;
    notes?: string;
    mappingMode?: string;
    basicDefinition?: string;
    basicSourceSystems?: string[];
    basicAggregation?: string;
    basicFormula?: string;
    basicScopeConditions?: string;
    referenceYear?: number;
    referenceValue?: string;
    referencePeriod?: string;
    referenceSourceNote?: string;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
}

export interface IProjectDashboardMetricScopeModel {
    uuid: string;
    scopeType?: ReferenceModel;
    value: string;
    sortOrder?: number;
    active?: boolean;
}

export type ProjectDatasetValidationStatus = "pending" | "ok" | "warn" | "fail" | "error";

export type ProjectDatasetValidationGateStatus = "open" | "partially_approved" | "approved";

export interface IProjectDatasetValidationQueryPreviewModel {
    sql?: string | null;
    errorKey?: string | null;
}

export interface IProjectDatasetValidationComputedValue {
    label: string;
    value?: number | string | null;
}

export interface IProjectDatasetValidationComputedValuesTable {
    columns: string[];
    rows: Record<string, string | null | undefined>[];
    totalRowCount?: number;
    truncated?: boolean;
}

export interface IProjectDatasetValidationModel {
    uuid: string;
    joinConfig?: string;
    filterConfig?: string;
    dateDataset?: string;
    dateTable?: string;
    dateField?: string;
    validationStatus?: ProjectDatasetValidationStatus;
    computedValue?: number;
    computedValues?: IProjectDatasetValidationComputedValue[];
    computedValuesTable?: IProjectDatasetValidationComputedValuesTable;
    diffPct?: number;
    validationMessage?: string;
    validationQuery?: string;
    lastValidatedAt?: string;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
}

export interface IProjectBuildabilityModel {
    uuid: string;
    buildabilityCode?: ReferenceModel;
    notes?: string;
    active?: boolean;
}

export interface IProjectMetricModellingDecisionModel {
    uuid: string;
    viewStrategy?: string;
    viewName?: string;
    viewNotes?: string;
    active?: boolean;
}

export interface IProjectDashboardModellingDecisionModel {
    uuid: string;
    defaultViewName?: string;
    active?: boolean;
}

export interface IProjectFieldModellingModel {
    uuid: string;
    datasetId: string;
    tableId: string;
    fieldName: string;
    modelName?: string;
    displayLabel?: string;
    sourceContext?: string;
    obsolete?: boolean;
    active?: boolean;
}

/** Fase 7/8: her-goedkeuring per blok (dashboard/metric) per fase — `DASHLY_PROJECT_PHASE_BLOCK_REVIEW`. */
export interface IProjectPhaseBlockReviewModel {
    uuid: string;
    phaseCode?: string;
    blockType?: string;
    blockUuid?: string;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    customerApprovedAt?: string;
    dashlyApprovedAt?: string;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
}

/** Fase 8: view-SQL snapshot per metric — `DASHLY_PROJECT_METRIC_SCOPE_APPROVAL`. */
export interface IProjectMetricScopeApprovalModel {
    uuid: string;
    viewName?: string;
    generatedSql?: string;
    confirmedSql?: string;
    previewValue?: number;
    previewDiffPct?: number;
    sqlSavedAt?: string;
    sourceSummary?: string;
    active?: boolean;
}

export type ProjectViewDefinitionSeedSource = "dashboard_default" | "metric_view" | "manual";

/** Fase 9: project-level view definition (SELECT body only). */
export interface IProjectViewDefinitionModel {
    uuid?: string;
    name: string;
    sqlText: string;
    ready?: boolean;
    sortOrder?: number;
    seedSource?: ProjectViewDefinitionSeedSource | string;
    sourceDashboardUuid?: string;
    sourceMetricUuid?: string;
    active?: boolean;
}

export interface IProjectViewDefinitionPayload {
    uuid?: string;
    name: string;
    sqlText: string;
    ready?: boolean;
    sortOrder?: number;
    seedSource?: ProjectViewDefinitionSeedSource | string;
    sourceDashboardUuid?: string;
    sourceMetricUuid?: string;
    active?: boolean;
}

export interface IProjectDashboardMetricVisualizationModel {
    uuid: string;
    graphType?: ReferenceModel;
    sortOrder?: number;
    layoutX?: number;
    layoutY?: number;
    layoutWidth?: number;
    layoutHeight?: number;
    layoutSequence?: number;
    active?: boolean;
}

export interface IProjectDashboardMetricModel {
    uuid: string;
    title: string;
    shortDefinition?: string;
    sourceSystemHint?: string;
    graphType?: ReferenceModel;
    graphTypes?: ReferenceModel[];
    visualizations?: IProjectDashboardMetricVisualizationModel[];
    priority?: ReferenceModel;
    sortOrder?: number;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
    /** Fase 10 dashboard opzetten. */
    setupStatus?: string;
    /** Fase dashboard_template — 64-col grid layout (null = not placed). */
    layoutX?: number;
    layoutY?: number;
    layoutWidth?: number;
    layoutHeight?: number;
    layoutSequence?: number;
    scopes?: IProjectDashboardMetricScopeModel[];
    technicalMapping?: IProjectTechnicalMappingModel;
    datasetValidation?: IProjectDatasetValidationModel;
    buildability?: IProjectBuildabilityModel;
    modellingDecision?: IProjectMetricModellingDecisionModel;
    scopeApproval?: IProjectMetricScopeApprovalModel;
    /** Fase 7 her-goedkeuring van dit metric-blok. */
    phaseBlockReview?: IProjectPhaseBlockReviewModel;
    /** Fase 8 her-goedkeuring van dit metric-blok. */
    scopePhaseBlockReview?: IProjectPhaseBlockReviewModel;
    linkedProcesses?: ReferenceModel[];
    linkedGaps?: ReferenceModel[];
    linkedProcessUuids?: string[];
    linkedGapUuids?: string[];
}

export interface IProjectDashboardTemplateMetricPayload {
    /** Visualization uuid (template tile id). */
    uuid: string;
    graphType?: ReferenceModel;
    layoutX?: number;
    layoutY?: number;
    layoutWidth?: number;
    layoutHeight?: number;
    layoutSequence?: number;
    /** When true, clear layout so the metric returns to the palette. */
    clearLayout?: boolean;
}

export interface IProjectDashboardTemplatePayload {
    metrics: IProjectDashboardTemplateMetricPayload[];
}

/** Fase 0 — Proces & gap-analyse: "Stappen" sub-list op een proces. */
export interface IProjectProcessStepModel {
    uuid: string;
    description: string;
    sortOrder?: number;
    active?: boolean;
}

/** Fase 0 — proces binnen een afdeling. */
export interface IProjectProcessModel {
    uuid: string;
    name: string;
    priority?: ReferenceModel;
    shortDescription?: string;
    frequency?: string;
    sortOrder?: number;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
    steps?: IProjectProcessStepModel[];
    linkedMetrics?: IProjectLinkedMetricModel[];
}

/** Fase 0 — ontbrekend inzicht ("gap") binnen een afdeling, optioneel gekoppeld aan processen. */
export interface IProjectGapModel {
    uuid: string;
    name: string;
    category?: ReferenceModel;
    priority?: ReferenceModel;
    painPoint?: string;
    impact?: string;
    proposedMetric?: string;
    proposedDashboard?: string;
    sortOrder?: number;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
    archived?: boolean;
    ownerUserUuid?: string;
    ownerName?: string;
    ownerEmail?: string;
    linkedProcesses?: ReferenceModel[];
    linkedMetrics?: IProjectLinkedMetricModel[];
}

export interface IProjectLinkedMetricModel {
    metricUuid: string;
    metricName?: string;
    dashboardUuid?: string;
    dashboardName?: string;
}

/** Fase 0 — afdeling (parent block) met processen en gaps. */
export interface IProjectDepartmentModel {
    uuid: string;
    name: string;
    ownerRole?: string;
    description?: string;
    sortOrder?: number;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
    processes?: IProjectProcessModel[];
    gaps?: IProjectGapModel[];
}

export interface IProjectDashboardModel {
    uuid: string;
    name: string;
    goalQuestion?: string;
    targetAudience?: string;
    sortOrder?: number;
    customerApproved?: boolean;
    dashlyApproved?: boolean;
    blockStatus?: ProjectBlockStatus;
    active?: boolean;
    /** Fase 10 dashboard opzetten. */
    setupStatus?: string;
    metrics?: IProjectDashboardMetricModel[];
    modellingDecision?: IProjectDashboardModellingDecisionModel;
    /** Fase 7 her-goedkeuring van dit dashboard-blok. */
    phaseBlockReview?: IProjectPhaseBlockReviewModel;
}

export type ProjectStakeholderGateStatus = "open" | "partially_approved" | "approved";

export type ProjectStakeholderStatusCode = "pending" | "approved" | "changes_requested";

export interface IProjectStakeholderModel {
    uuid: string;
    /** Fase waarvoor dit stakeholder-akkoord geldt — default `stakeholder_agreement` (fase 2). */
    phaseCode?: string;
    user?: ReferenceModel;
    name: string;
    email?: string;
    role?: ReferenceModel;
    status?: ReferenceModel;
    sourceCode?: string;
    /** Fase-2-stakeholder waaruit deze rij is geseed (fase 7/8). */
    sourceStakeholderUuid?: string;
    required?: boolean;
    statusComment?: string;
    approvedAt?: string;
    approvedByUserUuid?: string;
    sortOrder?: number;
    active?: boolean;
}

export interface IProjectModel {
    uuid: string;
    customer?: ReferenceModel;
    name: string;
    description?: string;
    currentPhase?: ReferenceModel;
    unlockedPhase?: ReferenceModel;
    processGapAnalysisProgressApproved?: number;
    processGapAnalysisProgressTotal?: number;
    processGapAnalysisPhaseComplete?: boolean;
    processGapAnalysisNamedOpenCount?: number;
    progressApproved?: number;
    progressTotal?: number;
    phaseComplete?: boolean;
    stakeholderProgressApproved?: number;
    stakeholderProgressTotal?: number;
    stakeholderPhaseComplete?: boolean;
    stakeholderGateStatus?: ProjectStakeholderGateStatus;
    dashboardTemplateProgressApproved?: number;
    dashboardTemplateProgressTotal?: number;
    dashboardTemplatePhaseComplete?: boolean;
    businessLogicProgressApproved?: number;
    businessLogicProgressTotal?: number;
    businessLogicPhaseComplete?: boolean;
    datasetValidationAProgressApproved?: number;
    datasetValidationAProgressTotal?: number;
    datasetValidationAPhaseComplete?: boolean;
    modellingDecisionProgressApproved?: number;
    modellingDecisionProgressTotal?: number;
    modellingDecisionPhaseComplete?: boolean;
    datasetValidationBProgressApproved?: number;
    datasetValidationBProgressTotal?: number;
    datasetValidationBPhaseComplete?: boolean;
    datasetValidationBGateStatus?: ProjectDatasetValidationGateStatus;
    datasetValidationBBlockProgressApproved?: number;
    datasetValidationBBlockProgressTotal?: number;
    datasetValidationBStakeholderProgressApproved?: number;
    datasetValidationBStakeholderProgressTotal?: number;
    scopeApprovalProgressApproved?: number;
    scopeApprovalProgressTotal?: number;
    scopeApprovalPhaseComplete?: boolean;
    scopeApprovalStakeholderGateStatus?: ProjectStakeholderGateStatus;
    scopeApprovalBlockProgressApproved?: number;
    scopeApprovalBlockProgressTotal?: number;
    scopeApprovalStakeholderProgressApproved?: number;
    scopeApprovalStakeholderProgressTotal?: number;
    dataMappingProgressApproved?: number;
    dataMappingProgressTotal?: number;
    dataMappingPhaseComplete?: boolean;
    dashboardSetupProgressApproved?: number;
    dashboardSetupProgressTotal?: number;
    dashboardSetupPhaseComplete?: boolean;
    validationFeedback?: IProjectValidationFeedbackModel[];
    dashboardValidationProgressApproved?: number;
    dashboardValidationProgressTotal?: number;
    dashboardValidationPhaseComplete?: boolean;
    live?: boolean;
    legacy?: boolean;
    dashboardLivePhaseComplete?: boolean;
    /** Open hire/help support requests; only populated for Support.View callers. */
    openSupportCount?: number;
    /** `phase` = active-fase nested only; `full` = complete nested graph. */
    nestedScope?: "phase" | "full";
    createdAt?: string;
    updatedAt?: string;
    active?: boolean;
    departments?: IProjectDepartmentModel[];
    dashboards?: IProjectDashboardModel[];
    stakeholders?: IProjectStakeholderModel[];
    fieldModelling?: IProjectFieldModellingModel[];
    viewDefinitions?: IProjectViewDefinitionModel[];
}

export interface IProjectLegacySourceMetricModel {
    dashboardItemUuid: string;
    metricUuid: string;
    title?: string;
    graphType?: ReferenceModel;
    sortOrder?: number;
}

export interface IProjectLegacySourceDashboardModel {
    uuid: string;
    name?: string;
    description?: string;
    metrics?: IProjectLegacySourceMetricModel[];
}

export interface IProjectLegacySourceModel {
    customer?: ReferenceModel;
    dashboards?: IProjectLegacySourceDashboardModel[];
}

export interface IProjectLegacyCreatePayload {
    customer: ReferenceModel;
    name?: string;
    dashboards: Array<{
        uuid: string;
        dashboardItemUuids: string[];
    }>;
}

export interface IProjectPayload {
    uuid?: string;
    customer?: ReferenceModel;
    name: string;
    description?: string;
    currentPhase?: ReferenceModel;
    active?: boolean;
    departments?: IProjectDepartmentPayload[];
    dashboards?: IProjectDashboardPayload[];
    stakeholders?: IProjectStakeholderPayload[];
    /** Scopes stakeholder sync to this phase; empty stakeholders clears that phase. */
    stakeholderSyncPhaseCode?: string;
    /** Party that re-approved before this save: keeps that badge, revokes the other. */
    approvalActorParty?: "customer" | "dashly";
    fieldModelling?: IProjectFieldModellingPayload[];
    viewDefinitions?: IProjectViewDefinitionPayload[];
}

export interface IProjectStakeholderPayload {
    uuid?: string;
    phaseCode?: string;
    user?: ReferenceModel;
    name?: string;
    email?: string;
    role?: ReferenceModel;
    required?: boolean;
    sortOrder?: number;
    active?: boolean;
}

export interface IProjectStakeholderStatusPayload {
    statusCode: ProjectStakeholderStatusCode;
    statusComment?: string;
}

export interface IProjectProcessStepPayload {
    uuid?: string;
    description: string;
    sortOrder?: number;
    active?: boolean;
}

export interface IProjectProcessPayload {
    uuid?: string;
    name: string;
    priority?: ReferenceModel;
    shortDescription?: string;
    frequency?: string;
    sortOrder?: number;
    active?: boolean;
    steps?: IProjectProcessStepPayload[];
}

export interface IProjectGapPayload {
    uuid?: string;
    name: string;
    priority?: ReferenceModel;
    painPoint?: string;
    impact?: string;
    proposedMetric?: string;
    proposedDashboard?: string;
    sortOrder?: number;
    active?: boolean;
    /** Many-to-many to sibling processes in the same department — by uuid, not the nested model. */
    linkedProcessUuids?: string[];
    categoryUuid?: string;
    archived?: boolean;
    ownerUserUuid?: string | null;
    ownerName?: string | null;
    ownerEmail?: string | null;
}

export interface IProjectDepartmentPayload {
    uuid?: string;
    name: string;
    ownerRole?: string;
    description?: string;
    sortOrder?: number;
    active?: boolean;
    processes?: IProjectProcessPayload[];
    gaps?: IProjectGapPayload[];
}

export interface IProjectDashboardPayload {
    uuid?: string;
    name: string;
    goalQuestion?: string;
    targetAudience?: string;
    sortOrder?: number;
    active?: boolean;
    setupStatus?: string;
    metrics?: IProjectDashboardMetricPayload[];
    modellingDecision?: IProjectDashboardModellingDecisionPayload;
}

export interface IProjectDashboardMetricPayload {
    uuid?: string;
    title: string;
    shortDefinition?: string;
    sourceSystemHint?: string;
    graphType?: ReferenceModel;
    graphTypes?: ReferenceModel[];
    priority?: ReferenceModel;
    sortOrder?: number;
    active?: boolean;
    setupStatus?: string;
    scopes?: IProjectDashboardMetricScopePayload[];
    technicalMapping?: IProjectTechnicalMappingPayload;
    datasetValidation?: IProjectDatasetValidationPayload;
    buildability?: IProjectBuildabilityPayload;
    modellingDecision?: IProjectMetricModellingDecisionPayload;
    linkedProcessUuids?: string[];
    linkedGapUuids?: string[];
}

export interface IProjectMetricModellingDecisionPayload {
    uuid?: string;
    viewStrategy?: string;
    viewName?: string;
    viewNotes?: string;
    active?: boolean;
}

export interface IProjectDashboardModellingDecisionPayload {
    uuid?: string;
    defaultViewName?: string;
    active?: boolean;
}

export interface IProjectFieldModellingPayload {
    uuid?: string;
    datasetId: string;
    tableId: string;
    fieldName: string;
    modelName?: string;
    displayLabel?: string;
    sourceContext?: string;
    obsolete?: boolean;
    active?: boolean;
}

export interface IProjectTechnicalFieldPayload {
    uuid?: string;
    fieldName: string;
    functionName?: string;
    groupBy?: boolean;
    sortOrder?: number;
    active?: boolean;
}

export interface IProjectTechnicalTablePayload {
    uuid?: string;
    datasetId: string;
    tableId: string;
    sortOrder?: number;
    active?: boolean;
    fields?: IProjectTechnicalFieldPayload[];
}

export interface IProjectTechnicalMappingPayload {
    uuid?: string;
    tables?: IProjectTechnicalTablePayload[];
    calculationConfig?: string;
    notes?: string;
    mappingMode?: string;
    basicDefinition?: string;
    basicSourceSystems?: string[];
    basicAggregation?: string;
    basicFormula?: string;
    basicScopeConditions?: string;
    referenceYear?: number;
    referenceValue?: string;
    referencePeriod?: string;
    referenceSourceNote?: string;
    active?: boolean;
}

export interface IProjectDatasetValidationPayload {
    uuid?: string;
    joinConfig?: string;
    filterConfig?: string;
    dateDataset?: string;
    dateTable?: string;
    dateField?: string;
    active?: boolean;
}

export interface IProjectBuildabilityPayload {
    uuid?: string;
    buildabilityCode?: ReferenceModel;
    notes?: string;
    active?: boolean;
}

export interface IProjectDashboardMetricScopePayload {
    uuid?: string;
    scopeType?: ReferenceModel;
    value: string;
    sortOrder?: number;
    active?: boolean;
}

export interface IProjectApprovePayload {
    approved?: boolean;
}

export interface IProjectHireRequestPayload {
    phaseCode: string;
    message?: string;
}

export interface IProjectHireRequestModel {
    uuid: string;
    phaseCode: string;
    message?: string;
    requesterName?: string;
    requesterUserUuid?: string;
    createdAt?: string;
    dashlyEmailUuid?: string;
    recipients?: string[];
}

export interface IProjectHelpRequestPayload {
    phaseCode: string;
    dashboardUuid: string;
    metricUuid?: string;
    message: string;
}

export interface IProjectHelpRequestModel {
    uuid: string;
    dashboardName?: string;
    metricName?: string;
    message?: string;
    requesterName?: string;
    requesterUserUuid?: string;
    createdAt?: string;
    dashlyEmailUuid?: string;
    recipients?: string[];
}

export interface IProjectSupportRequestModel {
    uuid: string;
    requestType: string;
    status: string;
    phaseCode: string;
    projectUuid: string;
    projectName?: string;
    customer?: ReferenceModel;
    dashboardUuid?: string;
    dashboardName?: string;
    metricUuid?: string;
    metricName?: string;
    scopeName?: string;
    message?: string;
    requesterUserUuid?: string;
    requesterName?: string;
    assignedUser?: ReferenceModel;
    dashlyEmailUuid?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface IProjectSupportRequestFilters {
    type?: string;
    status?: string;
    customer?: string;
    project?: string;
    search?: string;
}

export interface IProjectSupportOpenStatusModel {
    hasOpen: boolean;
}

export interface IProjectHelpOpenSummaryModel {
    phaseCodes: string[];
    dashboardUuids: string[];
    metricUuids: string[];
}

export const EMPTY_PROJECT_HELP_OPEN_SUMMARY: IProjectHelpOpenSummaryModel = {
    phaseCodes: [],
    dashboardUuids: [],
    metricUuids: [],
};


export interface IProjectValidationFeedbackModel {
    uuid?: string;
    dashboardUuid: string;
    metricUuid?: string;
    message: string;
    status?: string;
    sortOrder?: number;
    dashlyEmailUuid?: string;
    createdAt?: string;
    createdByName?: string;
    active?: boolean;
}

export interface IProjectValidationFeedbackPayload {
    dashboardUuid: string;
    metricUuid?: string;
    message: string;
    status?: string;
}

export interface IProjectValidationFeedbackStatusPayload {
    status: string;
}

export interface IProjectBuiltLinkModel {
    location?: string;
    locationIdentifier?: string;
    targets?: ReferenceModel[];
}

export interface IProjectBuiltLinkTargetsModel {
    scopedToLinkedDashboard?: boolean;
    targets?: ReferenceModel[];
}

export interface IProjectBuiltLinkPayload {
    location: string;
    locationIdentifier: string;
    targetUuid: string;
}

export interface IProjectLinkedInformationModel {
    location?: string;
    sourceUuid?: string;
    projectName?: string;
    sourceName?: string;
    dashboardName?: string;
    goalQuestion?: string;
    targetAudience?: string;
    shortDefinition?: string;
    sourceSystemHint?: string;
    priorityName?: string;
    visualization?: string;
    dimensions?: string[];
    filters?: string[];
    dashboardInformationHtml?: string;
    informationHtml?: string;
    businessDefinition?: string;
    businessSources?: string;
    businessAggregation?: string;
    businessFormula?: string;
    businessScopeConditions?: string;
    businessNotes?: string;
    referenceYear?: number;
    referencePeriod?: string;
    referenceValue?: string;
    referenceSourceNote?: string;
    calculationConfig?: string;
    processes?: string[];
    gaps?: string[];
    viewName?: string;
    viewNotes?: string;
    sourceTables?: string;
    buildabilityName?: string;
    buildabilityNotes?: string;
}

export interface IProjectListParams {
    search?: string;
    customer?: string;
    phase?: string;
    stage?: string;
    requiresApproval?: boolean;
    openSupport?: boolean;
    legacy?: boolean;
    includeInactive?: boolean;
    includeDeleted?: boolean;
    page?: number;
    size?: number;
}

export class ProjectService extends BaseService<IProjectModel, IProjectPayload> {
    public async fetchItems(params?: IProjectListParams): Promise<AxiosResponse<ApiResponse<Page<IProjectModel>>>> {
        return await api.get(`v1/${this.modelUrl()}`, { params });
    }

    public async fetchItem(
        uuid: string,
        options?: { nested?: "phase" | "full" },
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.get(`v1/${this.modelUrl()}/${uuid}`, {
            params: options?.nested ? { nested: options.nested } : undefined,
        });
    }

    public async approveDashboardCustomer(
        dashboardUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/dashboard/${dashboardUuid}/approve/customer`, { approved });
    }

    public async approveDashboardDashly(
        dashboardUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/dashboard/${dashboardUuid}/approve/dashly`, { approved });
    }

    public async approveMetricCustomer(
        metricUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/dashboard-metric/${metricUuid}/approve/customer`, { approved });
    }

    public async approveMetricDashly(
        metricUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/dashboard-metric/${metricUuid}/approve/dashly`, { approved });
    }

    /** Fase 0 — afdeling (parent block) goedkeuring. */
    public async approveDepartmentCustomer(
        departmentUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/department/${departmentUuid}/approve/customer`, { approved });
    }

    public async approveDepartmentDashly(
        departmentUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/department/${departmentUuid}/approve/dashly`, { approved });
    }

    /** Fase 0 — proces goedkeuring. */
    public async approveProcessCustomer(
        processUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/process/${processUuid}/approve/customer`, { approved });
    }

    public async approveProcessDashly(
        processUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/process/${processUuid}/approve/dashly`, { approved });
    }

    /** Fase 0 — gap goedkeuring. */
    public async approveGapCustomer(
        gapUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/gap/${gapUuid}/approve/customer`, { approved });
    }

    public async approveGapDashly(
        gapUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/gap/${gapUuid}/approve/dashly`, { approved });
    }

    public async approveTechnicalMappingCustomer(
        mappingUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/metric-technical-mapping/${mappingUuid}/approve/customer`, {
            approved,
        });
    }

    public async approveTechnicalMappingDashly(
        mappingUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/metric-technical-mapping/${mappingUuid}/approve/dashly`, {
            approved,
        });
    }

    public async approveDatasetValidationCustomer(
        validationUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/metric-dataset-validation/${validationUuid}/approve/customer`, {
            approved,
        });
    }

    public async approveDatasetValidationDashly(
        validationUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/metric-dataset-validation/${validationUuid}/approve/dashly`, {
            approved,
        });
    }

    public async advancePhase(projectUuid: string): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/advance-phase`);
    }

    public async submitHireRequest(
        projectUuid: string,
        payload: IProjectHireRequestPayload,
    ): Promise<AxiosResponse<ApiResponse<string>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/hire-request`, payload);
    }

    public async fetchHireRequests(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectHireRequestModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/hire-requests`);
    }

    public async submitHelpRequest(
        projectUuid: string,
        payload: IProjectHelpRequestPayload,
    ): Promise<AxiosResponse<ApiResponse<string>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/help-request`, payload);
    }

    public async fetchHelpRequests(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectHelpRequestModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/help-requests`);
    }

    public async fetchHelpOpenStatus(
        projectUuid: string,
        params: { dashboardUuid?: string; metricUuid?: string } = {},
    ): Promise<AxiosResponse<ApiResponse<IProjectSupportOpenStatusModel>>> {
        const query: Record<string, string> = {};
        if (params.dashboardUuid?.trim()) {
            query.dashboardUuid = params.dashboardUuid.trim();
        }
        if (params.metricUuid?.trim()) {
            query.metricUuid = params.metricUuid.trim();
        }
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/help-open-status`, { params: query });
    }

    public async fetchHireOpenStatus(
        projectUuid: string,
        phaseCode: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectSupportOpenStatusModel>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/hire-open-status`, {
            params: { phaseCode },
        });
    }

    public async fetchHelpOpenSummary(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectHelpOpenSummaryModel>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/help-open-summary`);
    }

    public async withdrawSupportRequest(
        supportRequestUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectSupportRequestModel>>> {
        return await api.put(`v1/${this.modelUrl()}/support-requests/${supportRequestUuid}/withdraw`);
    }

    public async fetchSupportRequests(
        filters: IProjectSupportRequestFilters = {},
    ): Promise<AxiosResponse<ApiResponse<IProjectSupportRequestModel[]>>> {
        const params: Record<string, string> = {};
        if (filters.type) {
            params.type = filters.type;
        }
        if (filters.status) {
            params.status = filters.status;
        }
        if (filters.customer) {
            params.customer = filters.customer;
        }
        if (filters.project) {
            params.project = filters.project;
        }
        if (filters.search) {
            params.search = filters.search;
        }
        return await api.get(`v1/${this.modelUrl()}/support-requests`, { params });
    }

    public async fetchSupportOpenCount(
        customerUuid?: string,
    ): Promise<AxiosResponse<ApiResponse<{ count: number }>>> {
        const params: Record<string, string> = {};
        if (customerUuid?.trim()) {
            params.customer = customerUuid.trim();
        }
        return await api.get(`v1/${this.modelUrl()}/support-requests/open-count`, { params });
    }

    public async fetchSupportAssignees(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/support-requests/assignees`);
    }

    public async updateSupportRequestStatus(
        supportRequestUuid: string,
        status: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectSupportRequestModel>>> {
        return await api.put(`v1/${this.modelUrl()}/support-requests/${supportRequestUuid}/status`, { status });
    }

    public async updateSupportRequestAssignee(
        supportRequestUuid: string,
        assignedUserUuid: string | null,
    ): Promise<AxiosResponse<ApiResponse<IProjectSupportRequestModel>>> {
        return await api.put(`v1/${this.modelUrl()}/support-requests/${supportRequestUuid}/assignee`, {
            assignedUserUuid,
        });
    }

    public async fetchValidationFeedback(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectValidationFeedbackModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/validation-feedback`);
    }

    public async createValidationFeedback(
        projectUuid: string,
        payload: IProjectValidationFeedbackPayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectValidationFeedbackModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/validation-feedback`, payload);
    }

    public async updateValidationFeedbackStatus(
        projectUuid: string,
        feedbackUuid: string,
        payload: IProjectValidationFeedbackStatusPayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectValidationFeedbackModel>>> {
        return await api.put(
            `v1/${this.modelUrl()}/${projectUuid}/validation-feedback/${feedbackUuid}`,
            payload,
        );
    }

    public async deleteValidationFeedback(
        projectUuid: string,
        feedbackUuid: string,
    ): Promise<AxiosResponse<ApiResponse<void>>> {
        return await api.delete(`v1/${this.modelUrl()}/${projectUuid}/validation-feedback/${feedbackUuid}`);
    }

    public async setProjectLive(
        projectUuid: string,
        live: boolean,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/set-live`, { live });
    }

    public async reopenDashboardValidation(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/reopen-dashboard-validation`);
    }

    public async finishDashboardValidation(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/finish-dashboard-validation`);
    }

    public async finishFunctionalDesign(projectUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/finish-functional-design`);
    }

    public async finishDatasetValidationB(projectUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/finish-dataset-validation-b`);
    }

    public async reopenDatasetValidationB(projectUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/reopen-dataset-validation-b`);
    }

    public async fetchPhaseCatalog(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/catalog/phases`);
    }

    public async fetchMetricPriorityCatalog(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/catalog/metric-priorities`);
    }

    public async fetchMetricScopeTypeCatalog(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/catalog/metric-scope-types`);
    }

    public async fetchStakeholderRoleCatalog(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/catalog/stakeholder-roles`);
    }

    /** Fase 0 — gap-category catalog ("Ontbrekend inzicht" dropdown). */
    public async fetchGapCategoryCatalog(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/catalog/gap-categories`);
    }

    public async updateStakeholderStatus(
        stakeholderUuid: string,
        payload: IProjectStakeholderStatusPayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectModel | boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/stakeholder/${stakeholderUuid}/status`, payload);
    }

    /** Stakeholder-only PUT — backend returns a light stakeholder snapshot (not a uuid string). */
    public async saveStakeholders(
        projectUuid: string,
        payload: IProjectPayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.put(`v1/${this.modelUrl()}/${projectUuid}`, payload);
    }

    public async runMetricDatasetValidation(
        metricUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectDatasetValidationModel>>> {
        return await api.post(`v1/project-dataset-validation/${metricUuid}/run`);
    }

    public async fetchMetricDatasetValidationQuery(
        metricUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectDatasetValidationQueryPreviewModel>>> {
        return await api.get(`v1/project-dataset-validation/${metricUuid}/query`);
    }

    public async fetchBuildabilityCodeCatalog(): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/catalog/buildability-codes`);
    }

    public async setMetricBuildability(
        metricUuid: string,
        payload: IProjectBuildabilityPayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectBuildabilityModel>>> {
        return await api.put(`v1/${this.modelUrl()}/dashboard-metric/${metricUuid}/buildability`, payload);
    }

    /** Fase 7/8: her-goedkeuring van een dashboard/metric-blok — eigen review-record per fase. */
    public async approvePhaseBlockReviewCustomer(
        reviewUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/phase-block-review/${reviewUuid}/approve/customer`, { approved });
    }

    public async approvePhaseBlockReviewDashly(
        reviewUuid: string,
        approved = true,
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.put(`v1/${this.modelUrl()}/phase-block-review/${reviewUuid}/approve/dashly`, { approved });
    }

    /** Fase 8: bevestig de gegenereerde view-SQL (verplicht vóór goedkeuren). */
    public async confirmMetricScopeApprovalSql(
        scopeApprovalUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectMetricScopeApprovalModel>>> {
        return await api.put(`v1/${this.modelUrl()}/metric-scope-approval/${scopeApprovalUuid}/confirm`);
    }

    /** Fase 8: run de dummy-preview van de view-SQL opnieuw (verversen vóór opslaan). */
    public async runMetricScopeApprovalPreview(
        scopeApprovalUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectMetricScopeApprovalModel>>> {
        return await api.post(`v1/${this.modelUrl()}/metric-scope-approval/${scopeApprovalUuid}/run`);
    }

    /** Fase 9: sync alle view-definities (full replace van actieve views). */
    public async syncViewDefinitions(
        projectUuid: string,
        views: IProjectViewDefinitionPayload[],
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.put(`v1/${this.modelUrl()}/${projectUuid}/view-definitions`, views);
    }

    /** Fase 9: idempotente seed uit modelleerbeslissing (als nog leeg). */
    public async seedDataMapping(
        projectUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.post(`v1/${this.modelUrl()}/${projectUuid}/data-mapping/seed`);
    }

    /** Fase dashboard_template: patch visualization layout. */
    public async saveDashboardTemplate(
        projectUuid: string,
        payload: IProjectDashboardTemplatePayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectModel>>> {
        return await api.put(`v1/${this.modelUrl()}/${projectUuid}/dashboard-template`, payload);
    }

    public async fetchLinkedBuiltInformation(
        resource: "dashboard" | "metric",
        resourceIdentifier: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectLinkedInformationModel[]>>> {
        return await api.get(`v1/${this.modelUrl()}/built-link-information`, {
            params: { resource, resourceIdentifier },
        });
    }

    public async fetchBuiltLink(
        projectUuid: string,
        location: string,
        locationIdentifier: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectBuiltLinkModel>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/built-link`, {
            params: { location, locationIdentifier },
        });
    }

    public async saveBuiltLink(
        projectUuid: string,
        payload: IProjectBuiltLinkPayload,
    ): Promise<AxiosResponse<ApiResponse<IProjectBuiltLinkModel>>> {
        return await api.put(`v1/${this.modelUrl()}/${projectUuid}/built-link`, payload);
    }

    public async clearBuiltLink(
        projectUuid: string,
        location: string,
        locationIdentifier: string,
        targetUuid: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectBuiltLinkModel>>> {
        return await api.delete(`v1/${this.modelUrl()}/${projectUuid}/built-link`, {
            params: { location, locationIdentifier, targetUuid },
        });
    }

    public async fetchBuiltLinkTargets(
        projectUuid: string,
        kind: "dashboard" | "metric",
        projectDashboardUuid?: string,
    ): Promise<AxiosResponse<ApiResponse<IProjectBuiltLinkTargetsModel>>> {
        const params: Record<string, string> = { kind };
        if (projectDashboardUuid?.trim()) {
            params.projectDashboardUuid = projectDashboardUuid.trim();
        }
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/built-link-targets`, { params });
    }

    public async exportJson(projectUuid: string): Promise<AxiosResponse<ApiResponse<unknown>>> {
        return await api.get(`v1/${this.modelUrl()}/${projectUuid}/export-json`);
    }

    public async fetchLegacySource(customerUuid: string): Promise<AxiosResponse<ApiResponse<IProjectLegacySourceModel>>> {
        return await api.get(`v1/${this.modelUrl()}/legacy/source`, { params: { customer: customerUuid } });
    }

    public async createLegacy(payload: IProjectLegacyCreatePayload): Promise<AxiosResponse<ApiResponse<string>>> {
        return await api.post(`v1/${this.modelUrl()}/legacy`, payload);
    }

    protected modelUrl(): string {
        return "project";
    }
}
